import { useCallback, type Dispatch, type SetStateAction } from "react";
import type { CmmdTaskStatus, EnvironmentId, ThreadId } from "@cmmd-forge/contracts";

import { toastManager } from "./ui/toast";
import type { WorkItem } from "./homebase/unifiedWorkItems";

export interface UseNoActiveThreadUiActionsInput {
  readonly isMobile: boolean;
  readonly workItemById: ReadonlyMap<string, WorkItem>;
  readonly navigateToThread: (input: {
    readonly environmentId: EnvironmentId;
    readonly threadId: ThreadId;
  }) => void;
  readonly setSelectedItemId: Dispatch<SetStateAction<string | null>>;
  readonly setActiveWorkspaceId: (workspaceId: string | null) => void;
  readonly setTeamFilter: (filter: string) => void;
  readonly setCampaignFilter: (filter: string) => void;
  readonly setProjectFilter: (filter: string) => void;
  readonly setStatusFilter: Dispatch<SetStateAction<"all" | CmmdTaskStatus>>;
  readonly setAssigneeFilter: (filter: string) => void;
  readonly setSearchQuery: (query: string) => void;
  readonly setReadyOnly: (readyOnly: boolean) => void;
  readonly setOpen?: ((open: boolean) => void | Promise<void>) | undefined;
  readonly setOpenMobile?: ((open: boolean) => void) | undefined;
}

export function useNoActiveThreadUiActions(input: UseNoActiveThreadUiActionsInput) {
  const {
    isMobile,
    workItemById,
    navigateToThread,
    setSelectedItemId,
    setActiveWorkspaceId,
    setTeamFilter,
    setCampaignFilter,
    setProjectFilter,
    setStatusFilter,
    setAssigneeFilter,
    setSearchQuery,
    setReadyOnly,
    setOpen,
    setOpenMobile,
  } = input;

  const handleNavigateToDependency = useCallback(
    (id: string) => {
      setSelectedItemId(id);
    },
    [setSelectedItemId],
  );

  const handleSelect = useCallback(
    (id: string) => {
      const item = workItemById.get(id);
      if (!item) return;
      if (item.threadId && item.environmentId) {
        navigateToThread({
          environmentId: item.environmentId,
          threadId: item.threadId,
        });
        return;
      }
      if (item.source === "cmmd-task") {
        setSelectedItemId(id);
        return;
      }
      if (isMobile && setOpenMobile) {
        setOpenMobile(true);
      } else if (setOpen) {
        void setOpen(true);
      }
      toastManager.add({
        type: "info",
        title: item.source === "cmmd-campaign" ? "Campaign selected" : "Project selected",
        description: "This Command Center item is read-only in phase 1.",
      });
    },
    [isMobile, navigateToThread, setOpen, setOpenMobile, setSelectedItemId, workItemById],
  );

  const handleClearFilters = useCallback(() => {
    setActiveWorkspaceId(null);
    setTeamFilter("all");
    setCampaignFilter("all");
    setProjectFilter("all");
    setStatusFilter("all");
    setAssigneeFilter("all");
    setSearchQuery("");
    setReadyOnly(false);
  }, [
    setActiveWorkspaceId,
    setAssigneeFilter,
    setCampaignFilter,
    setProjectFilter,
    setReadyOnly,
    setSearchQuery,
    setStatusFilter,
    setTeamFilter,
  ]);

  return {
    handleNavigateToDependency,
    handleSelect,
    handleClearFilters,
  };
}
